import React, { useState, useEffect, useMemo } from 'react';
import { FileText, AlertCircle, Eye, CheckCircle2, Clock, Check, X, RefreshCw, ChevronDown, Search, Users, Filter, RotateCcw } from 'lucide-react';
import poeService from '../../../services/poeService';
import { unitService } from '../../../services/unitService';
import { getAcademicYears, getSemesters } from '../../../services/programService';
import { getActiveEnrollmentContext } from '../../../services/enrollmentService';
import { useAuth } from '../../../contexts/AuthContext';
import POEApprovalWorkflow from './POEApprovalWorkflow';

/* ── tiny status pill ─────────────────────────────────────────── */
const Pill = ({ children, color = 'slate' }) => {
  const colors = {
    slate:   'bg-slate-100 text-slate-500',
    amber:   'bg-amber-50  text-amber-600  border border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    red:     'bg-red-50    text-red-600    border border-red-200',
    blue:    'bg-blue-50   text-blue-600   border border-blue-200',
    violet:  'bg-violet-50 text-violet-700 border border-violet-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${colors[color]}`}>
      {children}
    </span>
  );
};

const getItemStatus = (item) => {
  if (!item?.assessment_id) return { key: 'no_marks', label: 'No marks', color: 'slate' };
  if (!item?.evidence_id) return { key: 'no_upload', label: 'Not uploaded', color: 'slate' };
  if (item.workflow_stage === 'rejected') return { key: 'rejected', label: 'Returned', color: 'red' };
  if (item.workflow_stage === 'approved') return { key: 'approved', label: 'Approved', color: 'emerald' };
  if (item.workflow_stage === 'awaiting_trainer') return { key: 'awaiting_trainer', label: 'Awaiting review', color: 'amber' };
  if (item.workflow_stage === 'awaiting_final') return { key: 'awaiting_final', label: 'Awaiting final approval', color: 'violet' };
  return { key: 'awaiting_department', label: 'Forwarded', color: 'blue' };
};

const UUID_VALUE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const formatSemesterLabel = (semester) => {
  const raw = semester?.semester_name || semester?.name || '';
  if (raw && !UUID_VALUE.test(String(raw).trim())) return raw;

  const start = semester?.start_date ? new Date(`${semester.start_date}T00:00:00`) : null;
  const end = semester?.end_date ? new Date(`${semester.end_date}T00:00:00`) : null;
  if (start && end && !Number.isNaN(start.valueOf()) && !Number.isNaN(end.valueOf())) {
    const month = value => new Intl.DateTimeFormat('en', { month: 'short' }).format(value).toUpperCase();
    return `${month(start)}–${month(end)}${String(end.getFullYear()).slice(-2)}`;
  }
  if (semester?.term_number) return `Semester ${semester.term_number}`;
  return 'Semester';
};

/* ── status badge per cell ────────────────────────────────────── */
const StatusBadge = ({ item }) => {
  const status = getItemStatus(item);
  const Icon = status.key === 'approved' ? CheckCircle2 : status.key === 'rejected' ? X : Clock;
  return <Pill color={status.color}><Icon className="h-2.5 w-2.5" />{status.label}</Pill>;
};

/* ── main component ───────────────────────────────────────────── */
const TrainerPOEDashboard = ({ onBack = null }) => {
  const { profile } = useAuth();
  const [items, setItems]   = useState([]);
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [calendarYears, setCalendarYears] = useState([]);
  const [calendarSemesters, setCalendarSemesters] = useState([]);
  const defaultPeriod = React.useRef({ year: 'all', semester: 'all' });
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('all');
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState('all');
  const [selectedSemesterId, setSelectedSemesterId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [dashboardResult, trainerUnitsResult, yearsResult, semestersResult, periodResult] = await Promise.allSettled([
        poeService.getTrainerDashboardItems(),
        unitService.getAll({ trainerOnly: true, currentOnly: true }),
        getAcademicYears(),
        getSemesters(),
        getActiveEnrollmentContext(),
      ]);
      if (dashboardResult.status === 'rejected') throw dashboardResult.reason;
      const dashboardItems = dashboardResult.value || [];
      setItems(dashboardItems);
      setAssignedUnits(trainerUnitsResult.status === 'fulfilled' ? trainerUnitsResult.value || [] : []);
      setCalendarYears(yearsResult.status === 'fulfilled' ? yearsResult.value || [] : []);
      setCalendarSemesters(semestersResult.status === 'fulfilled' ? semestersResult.value || [] : []);
      const period = periodResult.status === 'fulfilled' && periodResult.value?.success
        ? periodResult.value.data
        : null;
      const hasActivePeriodRows = period?.academicYear?.id && period?.semester?.id
        && dashboardItems.some(item => (
          String(item.academic_year_id || '') === String(period.academicYear.id)
          && String(item.semester_id || '') === String(period.semester.id)
        ));
      const isShowingActivePeriod = period?.academicYear?.id && period?.semester?.id
        && String(selectedAcademicYearId) === String(period.academicYear.id)
        && String(selectedSemesterId) === String(period.semester.id);
      if (hasActivePeriodRows && defaultPeriod.current.year === 'all') {
        defaultPeriod.current = {
          year: String(period.academicYear.id),
          semester: String(period.semester.id),
        };
        setSelectedAcademicYearId(String(period.academicYear.id));
        setSelectedSemesterId(String(period.semester.id));
      } else if (!hasActivePeriodRows && (defaultPeriod.current.year === 'all' || isShowingActivePeriod)) {
        // Keep legacy rows visible when the database has not yet backfilled
        // their registration period. The user can still choose an explicit
        // period once period metadata is available.
        defaultPeriod.current = { year: 'all', semester: 'all' };
        setSelectedAcademicYearId('all');
        setSelectedSemesterId('all');
      }
    }
    catch (err) { setError(err?.message || 'Failed to load POE data.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [profile?.id]);

  const viewFile = async (item) => {
    try {
      const url = await poeService.getFilePreviewUrl(item.file_path);
      if (!url) throw new Error('File could not be opened.');
      setPreviewUrl(url);
      setPreviewTitle(`${item.student_name} - ${item.assessment_title}`);
    } catch (err) { setError(err?.message || 'Could not open file.'); }
  };

  const doReview = async (item, action, comments = '') => {
    try {
      setLoading(true);
      await poeService.review({ evidenceId: item.evidence_id, action, comments });
      await load();
    } catch (err) { setError(err?.message || 'Review failed.'); setLoading(false); }
  };

  const reviewSelectedSubmission = async (action, comments) => {
    if (!selectedSubmission) return;
    await doReview(selectedSubmission, action, comments);
    setSelectedSubmission(null);
  };

  /* ── derived data ─────────────────────────────────────────── */
  const getAssignedUnit = item => assignedUnits.find(unit => String(unit.id || unit.unit_id) === String(item.unit_id || item.id));
  const getProgramId = item => {
    const assignedUnit = getAssignedUnit(item);
    return item.program_id || item.programme_id || item.programId || item.program?.id
      || assignedUnit?.program_id || assignedUnit?.programId || assignedUnit?.program?.id || '';
  };
  const getProgramName = item => {
    const assignedUnit = getAssignedUnit(item);
    return item.programme_name || item.program_name || item.programme || item.program?.name
      || assignedUnit?.program?.name || '';
  };
  const getProgramCode = item => {
    const assignedUnit = getAssignedUnit(item);
    return item.programme_code || item.program_code || item.program?.code
      || assignedUnit?.program?.code || '';
  };
  // Keep period helpers scoped to period fields. Falling back to a generic
  // `item.id`/`item.name` made legacy POE rows look like they belonged to a
  // period even when they had no registration metadata, so changing a period
  // filter could leave the same records on screen.
  const getAcademicYearId = item => item.academic_year_id || item.academicYearId || item.year_id || '';
  const getAcademicYearName = item => item.academic_year_name || item.academicYearName || item.year_name || '';
  const getSemesterId = item => item.semester_id || item.semesterId || '';
  const getSemesterName = item => item.semester_name || item.semesterName || '';
  const getLevel = item => item.program_level || item.programLevel || item.level || item.program?.level || getAssignedUnit(item)?.program_level || getAssignedUnit(item)?.programLevel || '';
  const getModule = item => item.module_in_level || item.module || item.module_number || getAssignedUnit(item)?.module_in_level || getAssignedUnit(item)?.module || '';
  const matchesSelected = (selected, value) => selected === 'all'
    || (value !== null && value !== undefined && value !== '' && String(value) === String(selected));

  const programmes = useMemo(() => {
    const map = new Map();
    [...items, ...assignedUnits].forEach(item => {
      const id = String(getProgramId(item) || getProgramName(item));
      if (!id || id === 'undefined') return;
      map.set(id, {
        id,
        name: getProgramName(item) || 'Unnamed programme',
        code: getProgramCode(item),
      });
    });
    return Array.from(map.values()).sort((a, b) => `${a.code} ${a.name}`.localeCompare(`${b.code} ${b.name}`));
  }, [items, assignedUnits]);

  const academicYears = useMemo(() => {
    const map = new Map();
    calendarYears.forEach(item => {
      const id = String(item.id || item.academic_year_id || '');
      if (!id || id === 'undefined') return;
      const name = item.name || item.year_name || '';
      // The filter must reflect periods declared in System Settings only;
      // never create an option from an ID returned by a POE row.
      if (!name || UUID_VALUE.test(String(name).trim())) return;
      map.set(id, { id, name });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [calendarYears]);

  const semesters = useMemo(() => {
    const map = new Map();
    calendarSemesters.forEach(item => {
      const id = String(item.id || item.semester_id || '');
      if (!id || id === 'undefined') return;
      const rawName = item.semester_name || item.name || '';
      // POE rows can contain only a foreign key. They are not calendar
      // declarations and must not become options such as “Semester”.
      if (!rawName || UUID_VALUE.test(String(rawName).trim())) return;
      map.set(id, { id, name: formatSemesterLabel(item), termNumber: item.term_number });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [calendarSemesters]);

  const levels = useMemo(() => {
    const values = new Set([...items, ...assignedUnits].map(getLevel).filter(Boolean));
    return Array.from(values).sort((a, b) => Number(a) - Number(b));
  }, [items, assignedUnits]);

  const modules = useMemo(() => {
    const values = new Set([...items, ...assignedUnits].map(getModule).filter(Boolean));
    return Array.from(values).sort((a, b) => Number(a) - Number(b));
  }, [items, assignedUnits]);

  const periodAndProgrammeItems = useMemo(() => items.filter(item => {
    if (!matchesSelected(selectedProgramId, getProgramId(item) || getProgramName(item))) return false;
    if (!matchesSelected(selectedLevel, getLevel(item))) return false;
    if (!matchesSelected(selectedModule, getModule(item))) return false;
    if (!matchesSelected(selectedAcademicYearId, getAcademicYearId(item) || getAcademicYearName(item))) return false;
    if (!matchesSelected(selectedSemesterId, getSemesterId(item) || getSemesterName(item))) return false;
    return true;
  }), [items, selectedProgramId, selectedLevel, selectedModule, selectedAcademicYearId, selectedSemesterId]);

  const units = useMemo(() => {
    const map = new Map();
    const assignedScopeUnits = assignedUnits.filter(unit => matchesSelected(selectedProgramId, getProgramId(unit) || getProgramName(unit)) && matchesSelected(selectedLevel, getLevel(unit)) && matchesSelected(selectedModule, getModule(unit)));
    [...periodAndProgrammeItems, ...assignedScopeUnits].forEach(i => {
      const id = i.unit_id || i.id;
      if (!id || map.has(id)) return;
      map.set(id, { id, code: i.unit_code || i.code, name: i.unit_name || i.name });
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [periodAndProgrammeItems, assignedUnits, selectedProgramId, selectedLevel, selectedModule]);

  const activeItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return items.filter(item => {
      if (!matchesSelected(selectedProgramId, getProgramId(item) || getProgramName(item))) return false;
      if (!matchesSelected(selectedLevel, getLevel(item))) return false;
      if (!matchesSelected(selectedModule, getModule(item))) return false;
      if (!matchesSelected(selectedAcademicYearId, getAcademicYearId(item) || getAcademicYearName(item))) return false;
      if (!matchesSelected(selectedSemesterId, getSemesterId(item) || getSemesterName(item))) return false;
      if (query && ![item.student_name, item.student_number]
        .some(value => String(value || '').toLowerCase().includes(query))) return false;
      return true;
    });
  }, [items, selectedProgramId, selectedLevel, selectedModule, selectedAcademicYearId, selectedSemesterId, searchTerm]);

  const camSourceItems = useMemo(() => (
    periodAndProgrammeItems
  ), [periodAndProgrammeItems]);

  const cams = useMemo(() => {
    const resolved = [...new Map(camSourceItems
      .filter(item => item.assessment_title || item.assessment_number)
      .map(item => {
        const number = Number(item.assessment_number) || parseInt(String(item.assessment_title || '').match(/\d+/)?.[0] || '0', 10);
        return [number, { number, title: item.assessment_title || `CAM ${number}` }];
      })
      .filter(([number]) => number > 0))
      .values()].sort((a, b) => a.number - b.number);

    // A registered student can appear before any assessment row exists. Keep
    // the review surface useful in that case by showing the standard four CAMs.
    return resolved.length ? resolved : [1, 2, 3, 4].map(number => ({ number, title: `CAM ${number}` }));
  }, [camSourceItems]);

  const rows = useMemo(() => Object.values(
    activeItems.reduce((acc, item) => {
      const key = `${item.unit_id}_${item.student_id}_${getAcademicYearId(item)}_${getSemesterId(item)}`;
      if (!acc[key]) acc[key] = { student_id: item.student_id, student_number: item.student_number, student_name: item.student_name, unit_code: item.unit_code, cams: {} };
      const number = Number(item.assessment_number) || parseInt(String(item.assessment_title || '').match(/\d+/)?.[0] || '0', 10);
      if (number > 0) acc[key].cams[number] = item;
      return acc;
    }, {})
  ).sort((a, b) => {
    if (a.unit_code !== b.unit_code) return a.unit_code.localeCompare(b.unit_code);
    return String(a.student_number).localeCompare(String(b.student_number));
  }), [activeItems]);

  /* counts */
  const awaiting = activeItems.filter(i => i.workflow_stage === 'awaiting_trainer').length;
  const approved = activeItems.filter(i => i.workflow_stage === 'approved').length;
  const submitted = activeItems.filter(i => i.evidence_id).length;
  const studentCount = rows.length;

  /* ── render ───────────────────────────────────────────────── */
  return (
    <div className="space-y-4">
      {/* ── workspace header ── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            {onBack && <button onClick={onBack} className="whitespace-nowrap text-sm font-semibold text-blue-700 transition hover:underline">← Staff dashboard</button>}
            {onBack && <span className="text-slate-300">/</span>}
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Trainer workspace</p>
            <span className="text-slate-300">·</span>
            <h2 className="text-xl font-bold text-slate-900">POE review</h2>
            <Pill color="slate"><Users className="h-3 w-3" />{studentCount} students</Pill>
            <span className="hidden text-sm text-slate-500 xl:inline">Review marked scripts for your assigned units and forward approved evidence.</span>
          </div>
          <button onClick={load} className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
            <p className="text-xs font-medium text-slate-500">Assigned students</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{studentCount}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
            <p className="text-xs font-medium text-slate-500">CAM scripts submitted</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{submitted}</p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
            <p className="text-xs font-medium text-amber-700">Awaiting your review</p>
            <p className="mt-1 text-2xl font-bold text-amber-800">{awaiting}</p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3">
            <p className="text-xs font-medium text-emerald-700">Approved</p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">{approved}</p>
          </div>
        </div>
      </section>

      {/* ── error ── */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
        </div>
      )}

      {/* ── filters ── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Filter className="h-4 w-4 text-emerald-600" />
            Find submissions
          </div>
          {(selectedProgramId !== 'all' || selectedLevel !== 'all' || selectedModule !== 'all' || selectedAcademicYearId !== defaultPeriod.current.year || selectedSemesterId !== defaultPeriod.current.semester || searchTerm) && (
            <button
              onClick={() => {
                setSelectedProgramId('all');
                setSelectedLevel('all');
                setSelectedModule('all');
                setSelectedAcademicYearId(defaultPeriod.current.year);
                setSelectedSemesterId(defaultPeriod.current.semester);
                setSearchTerm('');
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          )}
        </div>
        <div className="grid min-w-0 grid-cols-6 gap-3">
          <label className="relative min-w-0">
            <span className="sr-only">Filter by programme</span>
            <select
              value={selectedProgramId}
              onChange={e => setSelectedProgramId(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="all">All programmes</option>
              {programmes.map(programme => <option key={programme.id} value={programme.id}>{programme.code ? `${programme.code} — ` : ''}{programme.name}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
          <label className="relative min-w-0">
            <span className="sr-only">Filter by level</span>
            <select
              value={selectedLevel}
              onChange={e => setSelectedLevel(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="all">All levels</option>
              {levels.map(level => <option key={level} value={level}>Level {level}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
          <label className="relative min-w-0">
            <span className="sr-only">Filter by module</span>
            <select
              value={selectedModule}
              onChange={e => setSelectedModule(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="all">All modules</option>
              {modules.map(module => <option key={module} value={module}>Module {module}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
          <label className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <span className="sr-only">Search students</span>
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search student or registration number"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </label>
          <label className="relative min-w-0">
            <span className="sr-only">Filter by semester</span>
            <select
              value={selectedSemesterId}
              onChange={e => setSelectedSemesterId(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="all">All semesters</option>
              {semesters.map(semester => <option key={semester.id} value={semester.id}>{semester.name}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
          <label className="relative min-w-0">
            <span className="sr-only">Filter by academic year</span>
            <select
              value={selectedAcademicYearId}
              onChange={e => setSelectedAcademicYearId(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="all">All academic years</option>
              {academicYears.map(year => <option key={year.id} value={year.id}>{year.name}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </label>
        </div>
      </section>

      {/* ── student review list ── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <FileText className="h-10 w-10 text-slate-200" />
            <p className="text-sm font-semibold text-slate-700">No submissions match these filters</p>
            <p className="text-xs text-slate-400">Try another programme, academic period, level, module or student.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Student CAM review list</h3>
                <p className="mt-0.5 text-xs text-slate-500">{rows.length} student{rows.length === 1 ? '' : 's'} in this view · {cams.length} CAM{cams.length === 1 ? '' : 's'} shown</p>
              </div>
              <Pill color="slate"><FileText className="h-3 w-3" />{activeItems.length} records</Pill>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-[1240px] w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="w-14 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">SN</th>
                    <th className="w-48 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">Student No</th>
                    <th className="w-64 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">Student Name</th>
                    {cams.map(cam => (
                      <th key={cam.number} className="min-w-[185px] px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">CAM {cam.number}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.map((row, index) => (
                    <tr key={`${row.student_id}_${row.unit_code}`} className="align-top transition-colors hover:bg-slate-50/60">
                      <td className="px-3 py-4 font-semibold text-slate-500">{index + 1}</td>
                      <td className="px-3 py-4 font-mono text-xs font-semibold text-slate-800">{row.student_number || '—'}</td>
                      <td className="px-3 py-4">
                        <p className="font-semibold text-slate-900">{row.student_name || 'Unnamed student'}</p>
                        <p className="mt-1 text-xs text-slate-500">{row.unit_code} · {units.find(unit => unit.code === row.unit_code)?.name || 'Assigned unit'}</p>
                      </td>
                      {cams.map(cam => {
                        const item = row.cams[cam.number];
                        const canAct = item?.evidence_id && item.workflow_stage === 'awaiting_trainer';
                        return (
                          <td key={cam.number} className="px-3 py-3">
                            <div className="flex min-h-[86px] flex-col items-start gap-2">
                              <StatusBadge item={item} />
                              {(item?.trainer_verified_at || item?.external_verified_at) && (
                                <div className="space-y-0.5 text-[10px] leading-tight text-slate-500">
                                  {item.trainer_verified_at && <p><span className="font-semibold text-slate-700">Internally verified by</span> {item.trainer_name || 'Trainer'} · {new Date(item.trainer_verified_at).toLocaleDateString('en-KE')}</p>}
                                  {item.external_verified_at && <p><span className="font-semibold text-slate-700">Externally verified by</span> {item.external_verifier_name || 'External verifier'} · {new Date(item.external_verified_at).toLocaleDateString('en-KE')}</p>}
                                </div>
                              )}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {item?.evidence_id && (
                                  <button
                                    onClick={() => setSelectedSubmission(item)}
                                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                                  >
                                    <Eye className="h-3 w-3" />
                                    View
                                  </button>
                                )}
                                {canAct && (
                                  <>
                                    <button
                                      onClick={() => doReview(item, 'approve')}
                                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white transition hover:bg-emerald-700"
                                    >
                                      <Check className="h-3 w-3" />
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => { if (window.confirm('Return this script to the student for correction?')) doReview(item, 'reject'); }}
                                      className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-600 transition hover:bg-red-50"
                                    >
                                      <X className="h-3 w-3" />
                                      Disapprove
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ── Document Preview Modal ── */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/90 backdrop-blur-sm">
          <div className="flex items-center justify-between bg-slate-900 px-6 py-4 shadow-lg border-b border-slate-800">
            <h3 className="text-sm font-semibold text-white">{previewTitle}</h3>
            <button
              onClick={() => { setPreviewUrl(null); setPreviewTitle(''); }}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 p-6">
            <iframe
              src={previewUrl}
              className="h-full w-full rounded-lg bg-white shadow-2xl"
              title="Document Preview"
            />
          </div>
        </div>
      )}
      <POEApprovalWorkflow
        isOpen={Boolean(selectedSubmission)}
        onClose={() => setSelectedSubmission(null)}
        submission={selectedSubmission}
        onSubmit={reviewSelectedSubmission}
        onViewFile={() => viewFile(selectedSubmission)}
        userRole="trainer_lecturer"
      />
    </div>
  );
};

export default TrainerPOEDashboard;
